import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { FaTrash, FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import CurrencyInput from './CurrencyInput';
import api from '../services/api';

/**
 * Reusable payment methods selector component.
 *
 * Props:
 *   paymentMethods       – array of { method, amount } (controlled state)
 *   setPaymentMethods    – state setter
 *   totalAmount          – number, total sale amount
 *   t                    – i18n translate function
 *   isCashReceivedEnabled – show "¿Con cuánto paga?" field (default true)
 *   formatCurrency       – function to format numbers as currency strings
 *   isLayaway            – whether this is a layaway/partial-payment sale (default true)
 *   disabled             – disable all inputs
 *   disabledMessage      – info message shown when disabled
 *   paymentMethodOptions – optional pre-loaded list from parent; if omitted, fetches from API
 */
const PaymentMethodsSelector = ({
    paymentMethods,
    setPaymentMethods,
    totalAmount = 0,
    t,
    isCashReceivedEnabled = true,
    formatCurrency,
    isLayaway = true,
    disabled = false,
    disabledMessage = '',
    paymentMethodOptions: propOptions,
}) => {
    const [paymentMethodOptions, setPaymentMethodOptions] = useState(
        propOptions || []
    );

    // Load from API only if not provided by parent
    useEffect(() => {
        if (propOptions) {
            setPaymentMethodOptions(propOptions);
            return;
        }
        api.get('/payment-methods')
            .then(res => setPaymentMethodOptions(res.data.paymentMethods || []))
            .catch(() => {
                // Fallback to hardcoded list if API fails
                setPaymentMethodOptions([
                    { value: 'Cash',          labelKey: 'salesPage.paymentMethods.cash',         defaultLabel: 'Efectivo' },
                    { value: 'Bank Transfer', labelKey: 'salesPage.paymentMethods.bankTransfer',  defaultLabel: 'Transferencia' },
                    { value: 'Card',          labelKey: 'salesPage.paymentMethods.card',          defaultLabel: 'Tarjeta' },
                    { value: 'Nequi',         labelKey: 'salesPage.paymentMethods.nequi',         defaultLabel: 'Nequi' },
                    { value: 'Daviplata',     labelKey: 'salesPage.paymentMethods.daviplata',     defaultLabel: 'Daviplata' },
                    { value: 'Other',         labelKey: 'salesPage.paymentMethods.other',         defaultLabel: 'Otro' },
                ]);
            });
    }, [propOptions]);

    const [globalCashReceived, setGlobalCashReceived] = useState('');

    // Venta directa con un único medio: el importe ES el total, y así debe estar en el ESTADO,
    // no sólo en el `value` del input (que además va deshabilitado en ese caso).
    // Sin esto el estado podía divergir de lo que se ve en pantalla: al pulsar "Agregar Medio de
    // Pago" el total se reparte entre los medios (14.300 → 7.150 cada uno) y `removeMethod` no
    // redistribuye, así que al borrar el segundo medio quedaba uno con 7.150 mientras el input
    // mostraba 14.300 — la venta se bloqueaba con "Pago incompleto" sin explicación visible.
    useEffect(() => {
        if (isLayaway || paymentMethods.length !== 1) return;
        const expected = (parseFloat(totalAmount) || 0).toFixed(2);
        if (paymentMethods[0].amount !== expected) {
            setPaymentMethods([{ ...paymentMethods[0], amount: expected }]);
        }
    }, [isLayaway, totalAmount, paymentMethods, setPaymentMethods]);

    const handleMethodChange = (index, field, value) => {
        const updated = [...paymentMethods];
        updated[index] = { ...updated[index], [field]: value };
        setPaymentMethods(updated);
    };

    const addMethod = () => {
        const usedMethods = new Set(paymentMethods.map(pm => pm.method));
        const firstUnused = paymentMethodOptions.find(opt => !usedMethods.has(opt.value));
        const defaultMethod = firstUnused ? firstUnused.value : 'Cash';

        const newMethods = [...paymentMethods, { method: defaultMethod, amount: '' }];
        const count = newMethods.length;
        const dividedAmount = ((parseFloat(totalAmount) || 0) / count).toFixed(2);

        setPaymentMethods(newMethods.map(pm => ({ ...pm, amount: dividedAmount })));
    };

    const removeMethod = (index) => {
        const remainingMethods = paymentMethods.filter((_, i) => i !== index);

        // En venta directa los medios deben sumar el total: al quitar uno hay que repartir de
        // nuevo, igual que hace addMethod. Si no, los importes divididos de cuando había más
        // medios se quedan pegados y la venta queda "incompleta" sin motivo aparente.
        // En abonos (isLayaway) no se toca: el importe lo decide el usuario.
        if (!isLayaway && remainingMethods.length > 0) {
            const dividedAmount = ((parseFloat(totalAmount) || 0) / remainingMethods.length).toFixed(2);
            setPaymentMethods(remainingMethods.map(pm => ({ ...pm, amount: dividedAmount })));
            return;
        }

        setPaymentMethods(remainingMethods);
    };

    const hasCashMethod = paymentMethods.some(pm => pm.method === 'Cash');
    const totalCashAmount = paymentMethods
        .filter(pm => pm.method === 'Cash')
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const totalPaid = paymentMethods.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const cashReceivedValue = parseFloat(globalCashReceived) || 0;
    const change = hasCashMethod && cashReceivedValue > totalCashAmount ? (cashReceivedValue - totalCashAmount) : 0;
    const remaining = totalAmount > 0 ? Math.max(0, totalAmount - totalPaid) : 0;

    // Resolve display label for an option (supports both API format and legacy labelKey format)
    const getOptionLabel = (opt) => {
        if (opt.labelKey && t) return t(opt.labelKey, opt.defaultLabel || opt.label || opt.value);
        return opt.label || opt.defaultLabel || opt.value;
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">{t('salesPage.paymentMethods.title', 'Medios de Pago')}</h6>
            </div>

            {disabled && disabledMessage && (
                <div className="alert alert-info mb-3" role="alert">
                    <small>{disabledMessage}</small>
                </div>
            )}

            {paymentMethods.map((pm, index) => {
                const otherPayments = paymentMethods.reduce(
                    (sum, p, i) => i !== index ? sum + (parseFloat(p.amount) || 0) : sum, 0
                );
                const maxPayable = Math.max(0, totalAmount - otherPayments); // eslint-disable-line no-unused-vars

                return (
                    <div key={index} className="mb-2 pb-2 border-bottom">
                        <Row className="align-items-center mb-1">
                            <Col xs={5}>
                                <Form.Select
                                    value={pm.method}
                                    onChange={(e) => {
                                        const updated = [...paymentMethods];
                                        updated[index] = { ...updated[index], method: e.target.value };
                                        setPaymentMethods(updated);
                                    }}
                                    size="sm"
                                    disabled={disabled}
                                >
                                    {paymentMethodOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {getOptionLabel(opt)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>

                            <Col xs={5}>
                                <CurrencyInput
                                    value={(!isLayaway && paymentMethods.length === 1) ? totalAmount : (pm.amount || '')}
                                    onChange={(value) => handleMethodChange(index, 'amount', value)}
                                    placeholder="0"
                                    size="sm"
                                    disabled={disabled || (!isLayaway && paymentMethods.length === 1)}
                                />
                            </Col>

                            <Col xs={2} className="text-end ps-0">
                                {paymentMethods.length > 1 && !disabled && (
                                    <Button variant="outline-danger" size="sm" onClick={() => removeMethod(index)}>
                                        <FaTrash />
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                );
            })}

            {!disabled && (
                <Button variant="outline-primary" size="sm" onClick={addMethod} className="mb-3 w-100">
                    <FaPlus className="me-1" /> {t('salesPage.paymentMethods.addMethod', 'Agregar Medio de Pago')}
                </Button>
            )}

            {/* Global Cash Received Field */}
            {hasCashMethod && isCashReceivedEnabled && (
                <div className="mb-3 p-3 bg-light rounded">
                    <Form.Group>
                        <Form.Label className="fw-bold">¿Con cuánto paga el cliente?</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                placeholder="Ingrese el monto recibido en efectivo"
                                value={globalCashReceived}
                                onChange={(e) => setGlobalCashReceived(e.target.value)}
                                className="fw-bold"
                            />
                        </InputGroup>
                        {change > 0.01 && (
                            <div className="text-end mt-2">
                                <small className="text-success fw-bold">
                                    Cambio a devolver: {formatCurrency(change)}
                                </small>
                            </div>
                        )}
                    </Form.Group>
                </div>
            )}

            <div className="d-flex justify-content-between pt-3 border-top">
                <div className="text-center">
                    <small className="text-muted d-block">{t('salesPage.saleModal.totalAmount')}</small>
                    <strong className="h5 text-primary">{formatCurrency(totalAmount)}</strong>
                </div>
                {isLayaway && (
                    <>
                        <div className="text-center border-start border-end px-3 mx-3">
                            <small className="text-muted d-block">{t('salesPage.paymentMethods.totalPaid', 'Total Abono')}</small>
                            <strong className="h5 text-success">{formatCurrency(totalPaid)}</strong>
                        </div>
                        <div className="text-center">
                            <small className="text-muted d-block">{t('salesPage.paymentMethods.remainingBalance', 'Saldo Pendiente')}</small>
                            <strong className={`h5 ${remaining > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatCurrency(Math.max(0, remaining))}
                            </strong>
                        </div>
                    </>
                )}
            </div>

            {!isLayaway && remaining > 0.01 && (
                <div className="alert alert-warning mt-3 mb-0 d-flex align-items-center" role="alert">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                        <strong>{t('salesPage.paymentMethods.incompletePaymentTitle', 'Pago incompleto')}</strong>
                        <br />
                        <small>{t('salesPage.paymentMethods.incompletePaymentMessage', 'Falta {{amount}} para completar el total de la venta.', { amount: formatCurrency(remaining) })}</small>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentMethodsSelector;
